import { useState, useEffect } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { CheckCircle2, Mail, ClipboardList, FileText, ArrowRight } from "lucide-react";
import { Button } from "./components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Toaster } from "./components/ui/sonner";

const Logo = "https://customer-assets.emergentagent.com/job_b7146a69-59d4-4e5f-9697-2f019e84fd69/artifacts/11kz9ncp_SM%20Logo%20dark%20text%20%282%29.png";

// Navigation (matches main page)
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-xl border-b border-violet-100 shadow-lg shadow-violet-500/5'
          : 'bg-transparent'
      }`}
      data-testid="thankyou-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="/">
            <motion.img
              src={Logo}
              alt="Spaceship Media"
              className="h-12 md:h-14"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            />
          </a>
        </div>
      </div>
    </motion.nav>
  );
};

// Privacy Policy Modal
const PrivacyModal = ({ isOpen, onClose }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-2xl bg-white max-h-[80vh] overflow-y-auto" data-testid="privacy-modal">
      <DialogHeader>
        <DialogTitle className="text-2xl font-semibold">Privacy Policy</DialogTitle>
        <DialogDescription className="text-slate-500">
          At Spaceship Media, we respect your privacy and are committed to protecting your personal information.
        </DialogDescription>
      </DialogHeader>
      <div className="mt-4 space-y-6 text-slate-700 text-sm leading-relaxed">
        <div>
          <h3 className="font-semibold text-slate-900 text-base mb-2">Information We Collect</h3>
          <p className="mb-2">When you register for the workshop, we may collect:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Business information (if provided)</li>
            <li>Payment details (processed securely via third-party providers)</li>
          </ul>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-base mb-2">How We Use Your Information</h3>
          <p className="mb-2">We use your information to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Confirm your workshop registration</li>
            <li>Communicate important updates</li>
            <li>Provide pre-workshop materials</li>
            <li>Improve our services and offerings</li>
          </ul>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-base mb-2">Data Sharing</h3>
          <p className="mb-2">We do not sell or share your personal information with third parties, except:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>With trusted service providers (such as payment processors)</li>
            <li>When required by law</li>
          </ul>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-base mb-2">Data Security</h3>
          <p>We take reasonable steps to protect your information from unauthorised access or misuse.</p>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-base mb-2">Contact</h3>
          <p>If you have any questions about this policy, please contact: <a href="mailto:launch@spaceshipmedia.com.au" className="text-violet-600 hover:underline">launch@spaceshipmedia.com.au</a></p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// Terms Modal
const TermsModal = ({ isOpen, onClose }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-2xl bg-white max-h-[80vh] overflow-y-auto" data-testid="terms-modal">
      <DialogHeader>
        <DialogTitle className="text-2xl font-semibold">Terms & Conditions</DialogTitle>
        <DialogDescription className="text-slate-500">
          By registering for the workshop, you agree to the following terms.
        </DialogDescription>
      </DialogHeader>
      <div className="mt-4 space-y-6 text-slate-700 text-sm leading-relaxed">
        <div>
          <h3 className="font-semibold text-slate-900 text-base mb-2">Workshop Participation</h3>
          <p>This is a hands-on workshop. While we aim to help every participant build a live website, results may vary depending on individual progress and preparation.</p>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-base mb-2">Payments</h3>
          <p>All payments are required to secure your seat. Prices are listed in AUD.</p>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-base mb-2">Refund Policy</h3>
          <p>Due to limited seats and preparation involved, registrations are non-refundable. However, you may transfer your spot to another person with prior notice.</p>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-base mb-2">Changes to Workshop</h3>
          <p>We reserve the right to reschedule or make changes to the workshop format if required. Participants will be notified in advance.</p>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-base mb-2">Responsibility</h3>
          <p>Participants are responsible for bringing required materials such as a laptop and any relevant assets.</p>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-base mb-2">Intellectual Property</h3>
          <p>All materials, frameworks, and processes shared during the workshop remain the intellectual property of Spaceship Media and are for personal use only.</p>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-base mb-2">Limitation of Liability</h3>
          <p>Spaceship Media is not liable for any business outcomes resulting from the use of knowledge gained during the workshop.</p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// Footer (matches main page)
const Footer = ({ onPrivacyClick, onTermsClick }) => (
  <footer className="relative bg-slate-950 text-white py-12 border-t border-violet-900/50 overflow-hidden" data-testid="footer">
    <div className="absolute inset-0 opacity-30">
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-violet-900/20 via-transparent to-pink-900/20" />
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <motion.img
          src={Logo}
          alt="Spaceship Media"
          className="h-10 brightness-0 invert"
          whileHover={{ scale: 1.05 }}
        />
        <p className="text-slate-400 text-sm">
          © {new Date().getFullYear()} Spaceship Media. All rights reserved.
        </p>
        <div className="flex gap-6">
          <button onClick={onPrivacyClick} className="text-slate-400 hover:text-violet-400 transition-colors text-sm">Privacy</button>
          <button onClick={onTermsClick} className="text-slate-400 hover:text-violet-400 transition-colors text-sm">Terms</button>
        </div>
      </div>
    </div>
  </footer>
);

// Next Steps data
const nextSteps = [
  {
    icon: Mail,
    title: "Check Your Email",
    desc: "Look out for a confirmation email from our team with all the details you need.",
    color: "from-violet-500 to-purple-600",
  },
  {
    icon: ClipboardList,
    title: "Complete the Questionnaire",
    desc: "Fill in the initial questionnaire so we can tailor the workshop to your needs.",
    color: "from-blue-500 to-cyan-600",
  },
  {
    icon: FileText,
    title: "Review the Checklist",
    desc: "Go through the pre-workshop checklist to make sure you're fully prepared on the day.",
    color: "from-pink-500 to-rose-600",
  },
];

export default function ThankYou() {
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  return (
    <div className="App">
      <Toaster position="top-center" />
      <Navigation />

      <main className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 pb-24">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-blue-50 to-pink-50" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-200/30 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-200/30 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-testid="thankyou-content">
          {/* Animated Check Icon */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
            className="mb-8"
          >
            <div className="inline-flex items-center justify-center w-28 h-28 rounded-full bg-gradient-to-br from-violet-500 via-blue-500 to-pink-500 shadow-2xl shadow-violet-500/30">
              <CheckCircle2 className="w-14 h-14 text-white" strokeWidth={2.5} />
            </div>
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mb-4"
            data-testid="thankyou-heading"
          >
            You're <span className="bg-gradient-to-r from-violet-600 via-blue-500 to-pink-500 bg-clip-text text-transparent">In!</span>
          </motion.h1>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-lg text-slate-500 mb-12 max-w-lg mx-auto"
          >
            Thank you for registering for the workshop — we can't wait to see you there. Here's what to do next:
          </motion.p>

          {/* Next Steps Cards */}
          <div className="grid gap-4 text-left mb-12">
            {nextSteps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + i * 0.15 }}
                className="flex items-start gap-4 bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-slate-100 shadow-lg"
                data-testid={`thankyou-step-${i + 1}`}
              >
                <div className={`flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
                  <step.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-base mb-1">{step.title}</h3>
                  <p className="text-sm text-slate-500">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Back to homepage */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
          >
            <a href="/">
              <Button
                className="bg-gradient-to-r from-violet-600 via-blue-500 to-pink-500 hover:from-violet-700 hover:via-blue-600 hover:to-pink-600 text-white rounded-full px-8 py-4 text-base font-medium shadow-xl shadow-violet-500/25 group"
                data-testid="thankyou-back-btn"
              >
                Back to Homepage
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>
      </main>

      <Footer
        onPrivacyClick={() => setIsPrivacyOpen(true)}
        onTermsClick={() => setIsTermsOpen(true)}
      />
      <PrivacyModal isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />
      <TermsModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
    </div>
  );
}
